import * as React from 'react';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender
} from '@tanstack/react-table';
import { checkIcon, modifierIcon } from '../../../../assets/icons';
import classes from './WorkingPlacesTable.module.scss';
import { FC, useEffect, useRef, useState } from 'react';
import { handleClickOutside } from '../../../../utils/Admin/handleClickOutside';
import { UsersWorkingPlacesType } from '../../../../types/Admin/WorkingPlacesTypes';
import { WorkingPlaceUpdateType } from '../../../../hooks/Admin/WorkingPlacesTable/usePutWorkingPlaceStatus';
import { ModalDeleteUse } from '../../Modals/ModalDeleteUse/ModalDeleteUse';
import { ActionMenu } from '../../ActionMenu/ActionMenuGeneral/ActionMenu';
import { ActionTypeInEditMenu } from '../../../../types/enums';
import Skeleton from '../../Skeleton/Skeleton';
import { AssignedServicesType } from '../../../../pagesAdmin/WorkplacesPage/WorkplacesPage';

export type workingPlaceType = {
  id: number;
  workingPlace: string;
  cabinet: number;
  number: number;
  assignedEmployees: UsersWorkingPlacesType[] | [];
  assignedServices?: AssignedServicesType[];
  boardIp: string;
  isActive: boolean;
  type: string;
  branchName?: string;
  queueName?: string;
  branches?: { id: number; name: string }[];
  queues?: { id: number; name: string }[];
};

const columnSizes = {
  workingPlaceWidth: 405,
  branchNameWidth: 180,
  typeWidth: 74,
  numberWidth: 72,
  assignedEmployeesWidth: 415,
  assignedServicesWidth: 415,
  boardIpWidth: 120,
  isActiveWidth: 94
};

const {
  workingPlaceWidth,
  branchNameWidth,
  typeWidth,
  numberWidth,
  assignedEmployeesWidth,
  assignedServicesWidth,
  boardIpWidth,
  isActiveWidth
} = columnSizes;

const columnHelper = createColumnHelper<workingPlaceType>();

type WorkingPlacesTableProps = {
  setActiveRowData: (data: workingPlaceType) => void;
  handleActiveModalOnEditBtn: () => void;
  workingPlacesData: workingPlaceType[] | [];
  deleteWorkingPlace: (id: number) => void;
  updateWorkingPalceStatus: (data: WorkingPlaceUpdateType) => void;
  isDeleteWorkingPlaceLoading: boolean;
  deleteModalActive: boolean;
  setDeleteModalActive: (value: boolean) => void;
  activeRowData: workingPlaceType;
  isLoadingData: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canChangeActivity?: boolean;
};

const WorkingPlacesTable: FC<WorkingPlacesTableProps> = ({
  setActiveRowData,
  handleActiveModalOnEditBtn,
  workingPlacesData,
  deleteModalActive,
  setDeleteModalActive,
  isDeleteWorkingPlaceLoading,
  deleteWorkingPlace,
  updateWorkingPalceStatus,
  activeRowData,
  isLoadingData,
  canEdit = true,
  canDelete = false,
  canChangeActivity = false
}) => {
  const [activeRow, setActiveRow] = React.useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const menuElement = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => handleClickOutside(event, menuElement, setOpenMenuId);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [setOpenMenuId]);

  const handleButtonClickModal = (data) => {
    setActiveRowData(data);
    handleActiveModalOnEditBtn();
    setOpenMenuId(false);
  };

  const handleClick = (id: string) => {
    setActiveRow(id);
  };

  const handleActivateStatus = (id: number, status: string) => {
    const data = { workingPlaceId: id, workingPlaceStatus: status };
    updateWorkingPalceStatus(data);
    setOpenMenuId(null);
  };

  const handleOpenDeleteModal = (data) => {
    setActiveRowData(data);
    setDeleteModalActive(true);
    setOpenMenuId(null);
  };

  const workingPlacesColumns = React.useMemo(() => [
    columnHelper.accessor('workingPlace', {
      header: () => <div className={classes.headerWorkingPlase}>Рабочее место</div>,
      cell: ({ getValue, row }) => (
        <div className={classes.cellWorkingPlace}>
          {(canEdit || canDelete || canChangeActivity) && (
            <div className={classes.editMenuContainer}>
              <img
                id={'imgMenu'}
                src={modifierIcon}
                alt="modifierService"
                className={classes.modifierImage}
                onClick={() => {
                  if (openMenuId === row?.original?.id) {
                    setOpenMenuId(null);
                  } else {
                    setOpenMenuId(row?.original?.id);
                  }
                }}
              />
              {openMenuId === row?.original?.id && (
                <div ref={menuElement} className={classes.actonMenuContainer}>
                  <ActionMenu
                    editOnClick={canEdit ? () => handleButtonClickModal(row?.original) : undefined}
                    isActivate={row.original.isActive}
                    activateDeactivateClick={
                      canChangeActivity
                        ? () =>
                            handleActivateStatus(
                              row?.original?.id,
                              row?.original?.isActive
                                ? ActionTypeInEditMenu.Deactivate
                                : ActionTypeInEditMenu.Activate
                            )
                        : undefined
                    }
                    deleteOnClick={canDelete ? () => handleOpenDeleteModal(row?.original) : undefined}
                  />
                </div>
              )}
            </div>
          )}
          <div className={classes.workingPlace}>{getValue()}</div>
        </div>
      ),
      size: workingPlaceWidth
    }),
    columnHelper.accessor('branchName', {
      cell: ({ row }) => (
        <div className={classes.cellWorkingPlace} title={row.original.branches?.map((b) => b.name).join(', ') || row.original.branchName}>
          {row.original.branches?.length ? row.original.branches.map((b) => b.name).join(', ') : (row.original.branchName || '-')}
        </div>
      ),
      header: () => <div className={classes.headerWorkingPlase}>Филиалы</div>,
      size: branchNameWidth
    }),
    columnHelper.accessor((row) => row.queues?.map((q) => q.name).join(', ') ?? row.queueName ?? '', {
      id: 'queues',
      cell: ({ row }) => (
        <div className={classes.cellWorkingPlace} title={row.original.queues?.map((q) => q.name).join(', ') || row.original.queueName}>
          {row.original.queues?.length ? row.original.queues.map((q) => q.name).join(', ') : (row.original.queueName || '-')}
        </div>
      ),
      header: () => <div className={classes.headerWorkingPlase}>Очереди</div>,
      size: branchNameWidth
    }),
    columnHelper.accessor('cabinet', {
      cell: ({ getValue }) => <div className={classes.contentCenter}>{getValue() || ''}</div>,
      header: () => <div className={classes.contentCenter}>Кабинет</div>,
      size: typeWidth
    }),
    columnHelper.accessor('number', {
      cell: ({ getValue }) => <div className={classes.contentCenter}>{getValue() || ''}</div>,
      header: () => <div className={classes.contentCenter}>Окно</div>,
      size: numberWidth
    }),
    columnHelper.accessor('assignedEmployees', {
      cell: ({ getValue }) => (
        <div className={classes.cellEmployees}>
          {(() => {
            const employees = getValue() ?? [];
            if (!employees.length) return <span>-</span>;
            return employees.map((item, index) => (
              <span key={item.id}>
                {employees.length > 1 && employees.length - 1 !== index
                  ? `${item.name}, `
                  : item.name}
              </span>
            ));
          })()}
        </div>
      ),
      header: () => <div className={classes.headerEmployees}>Закрепленные сотрудники</div>,
      size: assignedEmployeesWidth
    }),
    columnHelper.accessor('assignedServices', {
      cell: ({ getValue }) => {
        const services = getValue() ?? [];
        const hasServices = services.length > 0;

        return (
          <div className={classes.cellEmployees}>
            {hasServices ? (
              services.map((item, index) => (
                <span key={item.id}>
                  {services.length > 1 && services.length - 1 !== index
                    ? `${item.nameRu}, `
                    : item.nameRu}
                </span>
              ))
            ) : (
              <span>-</span>
            )}
          </div>
        );
      },
      header: () => <div className={classes.headerEmployees}>Закрепленные услуги</div>,
      size: assignedServicesWidth
    }),
    columnHelper.accessor('boardIp', {
      cell: ({ getValue }) => <div className={classes.contentCenter}>{getValue() || ''}</div>,
      header: () => <div className={classes.contentCenter}>IP табло</div>,
      size: boardIpWidth
    }),
    columnHelper.accessor('isActive', {
      cell: ({ getValue }) => {
        return getValue() ? (
          <div className={classes.cellIsActive}>
            <img src={checkIcon} alt="active" />
          </div>
        ) : (
          ''
        );
      },
      header: () => <div className={classes.contentCenter}>Активный</div>,
      size: isActiveWidth
    })
  ], [openMenuId, canEdit, canDelete, canChangeActivity]);

  const columns = workingPlacesColumns;

  const tableData = React.useMemo(
    () => (isLoadingData ? Array(5).fill({}) : workingPlacesData || []),
    [isLoadingData, workingPlacesData]
  );

  const tableColumns = React.useMemo(
    () =>
      isLoadingData
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton />
          }))
        : columns,
    [isLoadingData, columns]
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <div className={classes.container}>
      {deleteModalActive && (
        <ModalDeleteUse
          isActive={deleteModalActive}
          setActive={setDeleteModalActive}
          isDeleteLoading={isDeleteWorkingPlaceLoading}
          deleteItem={deleteWorkingPlace}
          id={activeRowData?.id}
        />
      )}
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            {table.getHeaderGroups()?.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{ width: `${header.getSize()}px` }}>
                      <div className={classes.thTitle}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        <div
                          {...{
                            onDoubleClick: () => header.column.resetSize(),
                            onMouseDown: header.getResizeHandler(),
                            className: `resizer ltr ${header.column.getIsResizing() ? 'isResizing' : ''}`
                          }}>
                          <div className={classes.colorResizer} />
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows?.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleClick(row.id)}
                className={activeRow === row.id ? classes.activeRow : ''}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkingPlacesTable;
